import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useParams } from 'react-router-dom';

import { useBillStatementsQuery } from 'apollo/queries/autogenerate/hooks';
import {
  AutoDebitSettlementStatus,
  BillStatementStatusV2,
} from 'apollo/queries/autogenerate/schemas';
import BillDetails from 'shared/modules/bill/components/organisms/BillDetails';
import BillStatements from 'shared/modules/bill/components/organisms/BillStatements';
import { ACTION, ITEM, SCREEN } from 'shared/modules/bill/constants/mixpanel';
import useHandleViewCharge from 'shared/modules/common/hooks/use-handle-view-charge';
import Mixpanel from 'shared/vendor/Mixpanel';
import { getCurrentInstallmentIndex } from 'utils/charge-utils';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';
import AppleChargeDetailsDialog, {
  useAppleChargeDetailsDialog,
} from 'web/modules/common/components/organisms/AppleChargeDetailsDialog';
import BillAutoDebitDetailsDialog from 'web/modules/common/components/organisms/BillAutoDebitDetailsDialog';
import InstallmentPlanDialog, {
  useInstallmentPlanDialog,
} from 'web/modules/common/components/organisms/InstallmentPlanDialog';
import ReceiptDialog, {
  useReceiptDialog,
} from 'web/modules/common/components/organisms/ReceiptDialog';
import routes from 'web/navigation/routes';

const BillSummaryScreen = () => {
  const { data } = useBillStatementsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  // We need to get oldest unpaid bill because we need to make sure the oldest bill is paid first
  const oldestUnpaidBillID = useMemo(
    () =>
      data?.billStatements.filter(
        (bill) => bill.statusV2 !== BillStatementStatusV2.PAID
      )[0]?.id || '',
    [data?.billStatements]
  );

  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const isHugeScreen = useMediaQuery({ minWidth: 1024 });
  const navigate = useNavigate();
  const { id } = useParams();

  const receiptDialog = useReceiptDialog();
  const installmentPlanDialog = useInstallmentPlanDialog();
  const appleChargeDetailsDialog = useAppleChargeDetailsDialog();
  const handleViewCharge = useHandleViewCharge({
    handleAppleCharge: (charge) => {
      appleChargeDetailsDialog.open(charge);
    },
    handleNPayCharge: (charge) => {
      const currentIndex = getCurrentInstallmentIndex(charge);

      installmentPlanDialog.open(charge.metadata.source, currentIndex);
    },
    handleStandardCharge: (charge) => {
      receiptDialog.open(charge);
    },
  });

  const [selectedBillId, setSelectedBillId] = useState('');
  const [
    showBillAutoDebitDetailsDialog,
    setShowBillAutoDebitDetailsDialog,
  ] = useState(false);

  const billDetail = (billId = '') => {
    if (
      billId.trim() &&
      data &&
      data.billStatements.findIndex((bill) => bill.id === billId) > -1
    ) {
      return (
        <BillDetails
          billId={billId}
          isOldestUnpaidBill={billId === oldestUnpaidBillID}
          onChargeItemPress={(charge) => handleViewCharge(charge)}
          onPayButtonPress={(bill) => {
            // If bill has autodebit and not overdue then there's no need to show settlement methodss
            if (
              bill.autoDebitSettlement &&
              bill.autoDebitSettlement.pullStatus !==
                AutoDebitSettlementStatus.FAILED
            ) {
              Mixpanel.trackEvent(`${SCREEN.STATEMENTS} - ${ACTION.CLICK}`, {
                Item: ITEM.AUTO_DEBIT_DETAILS,
              });

              setSelectedBillId(bill.id);
              setShowBillAutoDebitDetailsDialog(true);
            } else {
              Mixpanel.trackEvent(`${SCREEN.STATEMENTS} - ${ACTION.CLICK}`, {
                Item: ITEM.SETTLE,
              });

              navigate(
                routes.authenticated.home.billSettlement.replace(':id', bill.id)
              );
            }
          }}
          style={[
            style.billStatementDetails,
            isBigScreen && style.billStatementDetailsBigScreen,
            isHugeScreen && style.billStatementDetailsHugeScreen,
          ]}
        />
      );
    }

    return null;
  };

  // Record how long users see the BillSummary screen
  useEffect(() => {
    const eventName = `${SCREEN.STATEMENTS_HISTORY} - ${ACTION.PAGE_VIEW}`;

    Mixpanel.timeEvent(eventName);

    return () => Mixpanel.trackEvent(eventName);
  }, []);

  // Record how long users see individual bill statements
  useEffect(() => {
    const eventName = `${SCREEN.STATEMENTS} - ${ACTION.PAGE_VIEW}`;

    if (id) {
      Mixpanel.timeEvent(eventName);
    }

    return () => {
      // Initially, this will be undefined, as the user may not have selected a bill yet
      // We make sure we only track if user is actually looking at a bill statement
      if (id) {
        Mixpanel.trackEvent(eventName);
      }
    };
  }, [id]);

  const showBillStatements = !id?.trim() || (id && isHugeScreen);

  return (
    <View style={[style.screen, isBigScreen && style.screenBigScreen]}>
      <View
        style={[
          style.container,
          isBigScreen && style.containerBigScreen,
          isHugeScreen && style.containerHugeScreen,
        ]}
      >
        <View
          style={[
            isHugeScreen && style.billStatementsHugeScreen,
            !showBillStatements && { display: 'none' },
          ]}
        >
          <NavigationBar
            title="すべてのご請求"
            onBackButtonPress={() => navigate(routes.authenticated.home.root)}
            id="navigation-bar"
          />
          <BillStatements
            onItemPress={(item) =>
              navigate(
                routes.authenticated.home.billDetails.replace(':id', item.id)
              )
            }
          />
        </View>
        {id?.trim() && billDetail(id)}
        <ReceiptDialog
          charge={receiptDialog.charge}
          payment={receiptDialog.payment}
          open={receiptDialog.isOpen}
          onCloseButtonPress={receiptDialog.close}
        />
        {installmentPlanDialog.installmentPlanId && (
          <InstallmentPlanDialog
            installmentPlanId={installmentPlanDialog.installmentPlanId}
            currentInstallmentIndex={installmentPlanDialog.currInstallmentIndex}
            open={installmentPlanDialog.isOpen}
            onCloseButtonPress={installmentPlanDialog.close}
          />
        )}
        {appleChargeDetailsDialog.charge && (
          <AppleChargeDetailsDialog
            charge={appleChargeDetailsDialog.charge}
            open={appleChargeDetailsDialog.isOpen}
            onCloseButtonPress={appleChargeDetailsDialog.close}
          />
        )}
        <BillAutoDebitDetailsDialog
          billId={selectedBillId}
          open={showBillAutoDebitDetailsDialog}
          onOpen={() =>
            Mixpanel.timeEvent(
              `${SCREEN.AUTO_DEBIT_DETAILS} - ${ACTION.PAGE_VIEW}`
            )
          }
          onCloseButtonPress={() => setShowBillAutoDebitDetailsDialog(false)}
          onClose={() =>
            Mixpanel.trackEvent(
              `${SCREEN.AUTO_DEBIT_DETAILS} - ${ACTION.PAGE_VIEW}`
            )
          }
        />
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  // Template for building a new main screen
  // Scrolls on overflow for both mobile and desktop
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  screenBigScreen: {
    paddingHorizontal: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  containerHugeScreen: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  billStatementsHugeScreen: {
    width: '50%',
    paddingRight: 24,
  },
  billStatementDetails: {
    marginHorizontal: -16,
    marginTop: -24,
  },
  billStatementDetailsBigScreen: {
    marginHorizontal: 0,
    marginTop: 0,
  },
  billStatementDetailsHugeScreen: {
    width: '50%',
    marginTop: 64,
    borderRadius: 8,
    overflow: 'hidden',
  },
});

export default BillSummaryScreen;
